import type { BaseQLMiddleware } from '@adornis/baseql/metadata/types.js';
import { context } from '@adornis/baseql/server/context.js';
import { getMyself } from '@adornis/users/db/api/currentUserInfo.operations.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';

export const authenticatedMiddleware: BaseQLMiddleware = async options => {
  if (context.serverContext) return options.next();
  const user = await getMyself()({});
  if (!user) {
    throw new ExpectedError('Not authenticated');
  }
  return options.next();
};

/**
 * `authenticatedMiddleware` with a hole for named entity classes.
 *
 * The generic entity mutations take the entity itself as their only argument, so a login floor on
 * them is all-or-nothing per operation - and one genuinely anonymous flow needs a single class: the
 * public SuS form uploads its documents through `filesRouter`, which creates the `AdornisFile`
 * inside the uploader's own (logged out) session context rather than in a server context.
 *
 * Anonymous callers of an exempted class fall through to `context.entityPermissions`, which is the
 * layer that decides what a session without a user may write (`publicEntityPermissions`). Every
 * other class still needs a login.
 */
export const authenticatedUnlessEntityClassMiddleware =
  (anonymousEntityClasses: string[]): BaseQLMiddleware =>
  options => {
    const entityClass: string | undefined = options.args?.entity?._class;
    if (entityClass && anonymousEntityClasses.includes(entityClass)) return options.next();
    return authenticatedMiddleware(options);
  };
